import { EntityModules } from 'core/constants';

import { NotionStyleEditor } from 'containers/Tiptap/components/tiptap-templates/notion-style-editor';

import EntityDetailsTab from '../EntityDetailsTab';
import EntityRelatedTab from '../EntityRelatedTab';
import EntitySubmissionTab from '../EntitySubmissionTab';
import EntityAttachmentTab from '../EntityAttachmentTab';
import AssessmentTabWrapper from './components/AssessmentTabWrapper';
import { EntityTabsConfig } from './types';
import EntityHistoryTab from '../EntityHistoryTab';
import TrainingRecordsTab from '../TrainingRecordsTab';
import DocumentSettingsTab from '../DocumentSettingsTab';

import { ADMIN_AND_MANAGERS } from 'utils/roles/roleGroups';

import ControlAttachmentsTab from 'containers/ControlDetails/components/ControlAttachmentsTab';
import ControlRelatedTab from 'containers/ControlDetails/components/ControlRelatedTab';
import HazardRelatedTab from 'containers/HazardDetails/components/HazardRelatedTab';
import RiskControlsTab from 'containers/RisksDetails/components/RiskControlsTab';
import RiskDetailsTab from 'containers/RisksDetails/components/RiskDetailsTab';
import RiskHistoryTab from 'containers/RisksDetails/components/RiskHistoryTab';
import RiskScoringTab from 'containers/RisksDetails/components/RiskScoringTab';

import AuditAnswersTab from 'containers/AuditRecord/components/AuditAnswersTab';
import AuditRelatedTab from 'containers/AuditRecord/components/AuditRelatedTab';

export const ENTITY_TABS_CONFIG: EntityTabsConfig = {
  // Form first: an auditor opens a record to read answers, not metadata. Without an explicit entry
  // audits fall through to DEFAULT_TABS, which has no answers tab.
  [EntityModules.Audits]: [
    { label: 'Answers', id: 'form-tab', component: AuditAnswersTab },
    { label: 'Details', id: 'overview-tab', component: EntityDetailsTab },
    { label: 'Attachments', id: 'attachments-tab', component: EntityAttachmentTab },
    { label: 'Related', id: 'related-tab', component: AuditRelatedTab },
  ],
  [EntityModules.Events]: [
    {
      label: 'Details',
      id: 'overview-tab',
      component: EntityDetailsTab,
    },
    {
      label: 'Original Submission',
      id: 'original-submission-tab',
      component: EntitySubmissionTab,
    },
    {
      label: 'Attachments',
      id: 'attachments-tab',
      component: EntityAttachmentTab,
    },
    {
      label: 'History',
      id: 'history-tab',
      component: EntityHistoryTab,
    },
    {
      label: 'Related',
      id: 'related-tab',
      component: EntityRelatedTab,
    },
  ],
  [EntityModules.Controls]: [
    { label: 'Details', id: 'overview-tab', testId: 'control-tab-details', component: EntityDetailsTab },
    {
      label: 'History',
      id: 'history-tab',
      testId: 'control-tab-history',
      component: EntityHistoryTab,
      requiredRoles: ADMIN_AND_MANAGERS,
    },
    {
      label: 'Attachments',
      id: 'attachments-tab',
      testId: 'control-tab-attachments',
      component: ControlAttachmentsTab,
    },
    { label: 'Related', id: 'related-tab', testId: 'control-tab-related', component: ControlRelatedTab },
  ],
  [EntityModules.Hazards]: [
    { label: 'Details', id: 'overview-tab', testId: 'hazard-tab-details', component: EntityDetailsTab },
    {
      label: 'History',
      id: 'history-tab',
      testId: 'hazard-tab-history',
      component: EntityHistoryTab,
      requiredRoles: ADMIN_AND_MANAGERS,
    },
    { label: 'Related', id: 'related-tab', testId: 'hazard-tab-related', component: HazardRelatedTab },
  ],
  [EntityModules.RisksAssessments]: [
    {
      label: 'Details',
      id: 'overview-tab',
      component: EntityDetailsTab,
    },
    {
      label: 'Assessment',
      id: 'assessment-tab',
      component: AssessmentTabWrapper,
    },
    {
      label: 'Related',
      id: 'related-tab',
      component: EntityRelatedTab,
    },
  ],
  [EntityModules.Documents]: [
    {
      label: 'Document',
      id: 'documents-tab',
      component: NotionStyleEditor,
      // Stay mounted so the editor instance survives tab switches —
      // DocumentSettingsTab reads/writes the same editor's doc attrs.
      keepMounted: true,
    },
    {
      label: 'Related',
      id: 'related-tab',
      component: EntityRelatedTab,
    },
    {
      label: 'Settings',
      id: 'document-settings-tab',
      component: DocumentSettingsTab,
      requiredEditAccess: true,
      requiredFlag: 'documentsSettingsTabEnabled',
    },
  ],
  // With the Risks module flag off this reads as before: Details holds the scores, and the flagged tabs hide.
  [EntityModules.Risks]: [
    {
      label: 'Details',
      id: 'overview-tab',
      testId: 'risk-tab-details',
      component: EntityDetailsTab,
      hiddenByFlag: 'risksAssessmentsModuleEnabled',
    },
    {
      label: 'Details',
      id: 'overview-tab',
      testId: 'risk-tab-details',
      component: RiskDetailsTab,
      requiredFlag: 'risksAssessmentsModuleEnabled',
    },
    {
      label: 'Controls',
      id: 'controls-tab',
      testId: 'risk-tab-controls',
      component: RiskControlsTab,
      requiredFlag: 'risksAssessmentsModuleEnabled',
    },
    {
      label: 'Risk scoring',
      id: 'risk-scoring-tab',
      testId: 'risk-tab-risk-scoring',
      component: RiskScoringTab,
      requiredFlag: 'risksAssessmentsModuleEnabled',
    },
    { label: 'Attachments', id: 'attachments-tab', testId: 'risk-tab-attachments', component: EntityAttachmentTab },
    { label: 'History', id: 'history-tab', testId: 'risk-tab-history', component: RiskHistoryTab },
    { label: 'Related', id: 'related-tab', testId: 'risk-tab-related', component: EntityRelatedTab },
  ],
  [EntityModules.Contacts]: [
    {
      label: 'Details',
      id: 'overview-tab',
      component: EntityDetailsTab,
    },
    {
      label: 'Related',
      id: 'related-tab',
      component: EntityRelatedTab,
    },
    {
      label: 'Training Records',
      id: 'training-records-tab',
      component: TrainingRecordsTab,
      requiredFlag: 'trainingRecordsEnabled',
    },
  ],
};

// Default tabs for any other entity type
export const DEFAULT_TABS: EntityTabsConfig['default'] = [
  {
    label: 'Details',
    id: 'overview-tab',
    component: EntityDetailsTab,
  },
  {
    label: 'Related',
    id: 'related-tab',
    component: EntityRelatedTab,
  },
  {
    label: 'Attachments',
    id: 'attachments-tab',
    component: EntityAttachmentTab,
  },
];
